/**
 * Excel-like formula engine for MapleSheet.
 * Supports common functions, ranges like A1:B10, cell references, and basic conditionals.
 * Returns evaluated result as a string (numbers are stringified).
 */

type GetCellValue = (cellId: string) => string;

const COLUMN_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// Utilities
const colLetterToIndex = (letters: string): number => {
  const s = letters.toUpperCase();
  // Only A..Z supported, no AA, AB (MapleSheet currently uses up to Z)
  return COLUMN_LETTERS.indexOf(s);
};

const parseCell = (ref: string): { row: number; col: number } => {
  const match = ref.match(/^([A-Za-z]+)(\d+)$/);
  if (!match) throw new Error(`#REF! (${ref})`);
  const col = colLetterToIndex(match[1]);
  const row = parseInt(match[2], 10) - 1;
  if (col < 0 || row < 0) throw new Error(`#REF! (${ref})`);
  return { row, col };
};

const inBounds = (row: number, col: number): boolean => {
  return row >= 0 && col >= 0 && col < COLUMN_LETTERS.length;
};

const formatNumber = (num: number): string => {
  if (!isFinite(num)) return "#ERROR";
  return String(num);
};

const parseArgs = (argsStr: string): string[] => {
  // Split by commas respecting () and quotes
  const args: string[] = [];
  let buf = "";
  let depth = 0;
  let inQuotes = false;
  for (let i = 0; i < argsStr.length; i++) {
    const ch = argsStr[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      buf += ch;
    } else if (!inQuotes && ch === "(") {
      depth++;
      buf += ch;
    } else if (!inQuotes && ch === ")") {
      depth--;
      buf += ch;
    } else if (!inQuotes && depth === 0 && ch === ",") {
      args.push(buf.trim());
      buf = "";
    } else {
      buf += ch;
    }
  }
  if (buf.trim().length > 0) args.push(buf.trim());
  return args;
};

const stripQuotes = (s: string): string => {
  const m = s.match(/^"(.*)"$/);
  return m ? m[1] : s;
};

const coerceBoolean = (val: string): boolean => {
  const s = String(val).trim().toUpperCase();
  if (s === "TRUE") return true;
  if (s === "FALSE") return false;
  // non-empty non-zero strings considered true when used as boolean
  const n = parseFloat(val);
  if (!isNaN(n)) return n !== 0;
  return s.length > 0;
};

const toNumber = (val: string): number => {
  if (val === undefined || val === null) return 0;
  const s = String(val).trim();
  if (s.toUpperCase() === "TRUE") return 1;
  if (s.toUpperCase() === "FALSE") return 0;
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
};

const compareWithCriteria = (valueRaw: string, criteria: string): boolean => {
  const vStr = String(valueRaw ?? "");
  const vNum = toNumber(vStr);
  const c = String(criteria).trim();
  // Criteria like: >10, >=5, <3, <=2, =text, <>text
  const opMatch = c.match(/^(>=|<=|<>|=|>|<)(.*)$/);
  if (opMatch) {
    const op = opMatch[1];
    const rhsRaw = opMatch[2].trim();
    const rhsNum = toNumber(rhsRaw);
    const rhsStr = stripQuotes(rhsRaw);
    switch (op) {
      case ">": return vNum > rhsNum;
      case "<": return vNum < rhsNum;
      case ">=": return vNum >= rhsNum;
      case "<=": return vNum <= rhsNum;
      case "=":
        // if rhs is numeric-like compare numbers; else compare strings case-sensitive
        if (!isNaN(rhsNum) && rhsRaw !== "") return vNum === rhsNum;
        return vStr === rhsStr;
      case "<>":
        if (!isNaN(rhsNum) && rhsRaw !== "") return vNum !== rhsNum;
        return vStr !== rhsStr;
      default: return false;
    }
  }
  // Without operator -> equals
  return vStr === stripQuotes(c);
};

const cellIdFrom = (row: number, col: number): string => {
  return `${COLUMN_LETTERS[col]}${row + 1}`;
};

// Expand range like A1:B3 into list of cell IDs
const expandRangeCells = (range: string): string[] => {
  const m = range.match(/^([A-Za-z]+\d+):([A-Za-z]+\d+)$/);
  if (!m) {
    // single cell
    const single = range.trim();
    if (/^[A-Za-z]+\d+$/.test(single)) return [single];
    return []; // not a cell reference
  }
  const start = parseCell(m[1]);
  const end = parseCell(m[2]);
  const minRow = Math.min(start.row, end.row);
  const maxRow = Math.max(start.row, end.row);
  const minCol = Math.min(start.col, end.col);
  const maxCol = Math.max(start.col, end.col);
  const ids: string[] = [];
  for (let r = minRow; r <= maxRow; r++) {
    for (let c = minCol; c <= maxCol; c++) {
      if (inBounds(r, c)) ids.push(cellIdFrom(r, c));
    }
  }
  return ids;
};

const getArgValues = (rawArg: string, getCell: GetCellValue): string[] => {
  // Arg can be a range, a cell, a string literal, or expression
  const s = rawArg.trim();
  // Range
  if (/^[A-Za-z]+\d+:[A-Za-z]+\d+$/.test(s)) {
    const ids = expandRangeCells(s);
    return ids.map(getCell);
  }
  // Cell
  if (/^[A-Za-z]+\d+$/.test(s)) {
    return [getCell(s)];
  }
  // String literal or numeric
  if (/^".*"$/.test(s)) {
    return [stripQuotes(s)];
  }
  // Evaluate inner expression (e.g., 1+2, IF(...), etc.)
  return [evaluateFormulaInternal(s, getCell)];
};

const flattenToNumbers = (args: string[], getCell: GetCellValue): number[] => {
  const out: number[] = [];
  for (const a of args) {
    const vals = getArgValues(a, getCell);
    for (const v of vals) {
      const n = toNumber(v);
      if (!isNaN(n)) out.push(n);
    }
  }
  return out;
};

const flattenToStrings = (args: string[], getCell: GetCellValue): string[] => {
  const out: string[] = [];
  for (const a of args) {
    const vals = getArgValues(a, getCell);
    out.push(...vals.map((v) => String(v ?? "")));
  }
  return out;
};

const percentile = (arr: number[], k: number): number => {
  if (arr.length === 0) return NaN;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = (sorted.length - 1) * k;
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (idx - lower);
};

// Date helpers
const parseDate = (val: string): Date | null => {
  const s = String(val).trim();
  const n = parseFloat(s);
  if (!isNaN(n) && s !== "") {
    // treat as epoch milliseconds if big; else days offset? Simplify: epoch ms
    const d = new Date(n);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const formatTime = (d: Date): string => {
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return `${h}:${m}:${s}`;
};

// Evaluator
function callFunction(fnName: string, argsStr: string, getCell: GetCellValue, scopeVars?: Record<string, string>): string {
  const name = fnName.toUpperCase();
  const args = parseArgs(argsStr);

  // LET support (simple): LET(name, value, name2, value2, ..., resultExpression)
  if (name === "LET") {
    const vars: Record<string, string> = { ...(scopeVars || {}) };
    if (args.length < 2) return "#ERROR";
    for (let i = 0; i < args.length - 1; i += 2) {
      const varName = args[i];
      const varValRaw = args[i + 1];
      const val = evaluateFormulaInternal(varValRaw, getCell, vars);
      vars[stripQuotes(varName)] = val;
    }
    const resultExpr = args[args.length - 1];
    return evaluateFormulaInternal(resultExpr, getCell, vars);
  }

  switch (name) {
    // Math & Basic
    case "SUM": {
      const nums = flattenToNumbers(args, getCell);
      const total = nums.reduce((a, b) => a + b, 0);
      return formatNumber(total);
    }
    case "SUMIF": {
      // SUMIF(range, criteria, [sum_range])
      if (args.length < 2) return "#ERROR";
      const rangeVals = flattenToStrings([args[0]], getCell);
      const criteria = stripQuotes(args[1]);
      const sumVals = args[2] ? flattenToNumbers([args[2]], getCell) : rangeVals.map(toNumber);
      let sum = 0;
      for (let i = 0; i < rangeVals.length; i++) {
        if (compareWithCriteria(rangeVals[i], criteria)) sum += sumVals[i] ?? 0;
      }
      return formatNumber(sum);
    }
    case "SUMIFS": {
      // SUMIFS(sum_range, criteria_range1, criteria1, ...)
      if (args.length < 3) return "#ERROR";
      const sumRange = flattenToNumbers([args[0]], getCell);
      let mask = sumRange.map(() => true);
      for (let i = 1; i < args.length; i += 2) {
        const critRangeVals = flattenToStrings([args[i]], getCell);
        const crit = stripQuotes(args[i + 1]);
        mask = mask.map((m, idx) => m && compareWithCriteria(critRangeVals[idx], crit));
      }
      const sum = sumRange.reduce((acc, v, idx) => acc + (mask[idx] ? v : 0), 0);
      return formatNumber(sum);
    }
    case "AVERAGE": {
      const nums = flattenToNumbers(args, getCell);
      if (nums.length === 0) return "#DIV/0!";
      return formatNumber(nums.reduce((a, b) => a + b, 0) / nums.length);
    }
    case "AVERAGEIF": {
      if (args.length < 2) return "#ERROR";
      const rangeVals = flattenToStrings([args[0]], getCell);
      const criteria = stripQuotes(args[1]);
      const avgVals = args[2] ? flattenToNumbers([args[2]], getCell) : rangeVals.map(toNumber);
      const selected: number[] = [];
      for (let i = 0; i < rangeVals.length; i++) {
        if (compareWithCriteria(rangeVals[i], criteria)) selected.push(avgVals[i] ?? 0);
      }
      if (selected.length === 0) return "#DIV/0!";
      return formatNumber(selected.reduce((a, b) => a + b, 0) / selected.length);
    }
    case "AVERAGEIFS": {
      if (args.length < 3) return "#ERROR";
      const base = flattenToNumbers([args[0]], getCell);
      let mask = base.map(() => true);
      for (let i = 1; i < args.length; i += 2) {
        const rv = flattenToStrings([args[i]], getCell);
        const crit = stripQuotes(args[i + 1]);
        mask = mask.map((m, idx) => m && compareWithCriteria(rv[idx], crit));
      }
      const selected = base.filter((_, idx) => mask[idx]);
      if (selected.length === 0) return "#DIV/0!";
      return formatNumber(selected.reduce((a, b) => a + b, 0) / selected.length);
    }
    case "COUNT": {
      const nums = flattenToNumbers(args, getCell);
      return formatNumber(nums.length);
    }
    case "COUNTA": {
      const strs = flattenToStrings(args, getCell);
      return formatNumber(strs.filter((s) => String(s).trim().length > 0).length);
    }
    case "COUNTIF": {
      if (args.length < 2) return "#ERROR";
      const vals = flattenToStrings([args[0]], getCell);
      const crit = stripQuotes(args[1]);
      return formatNumber(vals.filter((v) => compareWithCriteria(v, crit)).length);
    }
    case "COUNTIFS": {
      if (args.length < 2) return "#ERROR";
      // Build mask based on first range length
      const firstRange = flattenToStrings([args[0]], getCell);
      let mask = firstRange.map(() => true);
      for (let i = 0; i < args.length; i += 2) {
        const rv = flattenToStrings([args[i]], getCell);
        const crit = stripQuotes(args[i + 1]);
        mask = mask.map((m, idx) => m && compareWithCriteria(rv[idx], crit));
      }
      return formatNumber(mask.filter(Boolean).length);
    }
    case "MAX": {
      const nums = flattenToNumbers(args, getCell);
      return formatNumber(nums.length ? Math.max(...nums) : NaN);
    }
    case "MIN": {
      const nums = flattenToNumbers(args, getCell);
      return formatNumber(nums.length ? Math.min(...nums) : NaN);
    }
    case "ROUND": {
      const val = toNumber(evaluateFormulaInternal(args[0] ?? "0", getCell));
      const digits = toNumber(evaluateFormulaInternal(args[1] ?? "0", getCell));
      const m = Math.pow(10, digits);
      return formatNumber(Math.round(val * m) / m);
    }
    case "ROUNDUP": {
      const val = toNumber(evaluateFormulaInternal(args[0] ?? "0", getCell));
      const digits = toNumber(evaluateFormulaInternal(args[1] ?? "0", getCell));
      const m = Math.pow(10, digits);
      return formatNumber(Math.ceil(val * m) / m);
    }
    case "ROUNDDOWN": {
      const val = toNumber(evaluateFormulaInternal(args[0] ?? "0", getCell));
      const digits = toNumber(evaluateFormulaInternal(args[1] ?? "0", getCell));
      const m = Math.pow(10, digits);
      return formatNumber(Math.floor(val * m) / m);
    }
    case "CEILING": {
      const val = toNumber(evaluateFormulaInternal(args[0] ?? "0", getCell));
      const sig = toNumber(evaluateFormulaInternal(args[1] ?? "1", getCell));
      return formatNumber(Math.ceil(val / sig) * sig);
    }
    case "FLOOR": {
      const val = toNumber(evaluateFormulaInternal(args[0] ?? "0", getCell));
      const sig = toNumber(evaluateFormulaInternal(args[1] ?? "1", getCell));
      return formatNumber(Math.floor(val / sig) * sig);
    }
    case "ABS": return formatNumber(Math.abs(toNumber(evaluateFormulaInternal(args[0] ?? "0", getCell))));
    case "SQRT": return formatNumber(Math.sqrt(toNumber(evaluateFormulaInternal(args[0] ?? "0", getCell))));
    case "RAND": return formatNumber(Math.random());
    case "RANDBETWEEN": {
      const min = Math.floor(toNumber(evaluateFormulaInternal(args[0] ?? "0", getCell)));
      const max = Math.floor(toNumber(evaluateFormulaInternal(args[1] ?? "0", getCell)));
      const r = Math.floor(Math.random() * (max - min + 1)) + min;
      return formatNumber(r);
    }

    // Text
    case "CONCAT":
    case "CONCATENATE": {
      const strs = flattenToStrings(args, getCell);
      return strs.join("");
    }
    case "TEXTJOIN": {
      // TEXTJOIN(delimiter, ignore_empty, text1, text2, ...)
      if (args.length < 3) return "#ERROR";
      const delimiter = stripQuotes(evaluateFormulaInternal(args[0], getCell));
      const ignoreEmpty = coerceBoolean(evaluateFormulaInternal(args[1], getCell));
      const parts = flattenToStrings(args.slice(2), getCell);
      return parts.filter((p) => (ignoreEmpty ? p.trim().length > 0 : true)).join(delimiter);
    }
    case "LEFT": {
      const text = stripQuotes(evaluateFormulaInternal(args[0] ?? '""', getCell));
      const n = Math.max(1, Math.floor(toNumber(evaluateFormulaInternal(args[1] ?? "1", getCell))));
      return text.substring(0, n);
    }
    case "RIGHT": {
      const text = stripQuotes(evaluateFormulaInternal(args[0] ?? '""', getCell));
      const n = Math.max(1, Math.floor(toNumber(evaluateFormulaInternal(args[1] ?? "1", getCell))));
      return text.substring(Math.max(0, text.length - n));
    }
    case "MID": {
      const text = stripQuotes(evaluateFormulaInternal(args[0] ?? '""', getCell));
      const start = Math.max(1, Math.floor(toNumber(evaluateFormulaInternal(args[1] ?? "1", getCell))));
      const n = Math.max(0, Math.floor(toNumber(evaluateFormulaInternal(args[2] ?? "0", getCell))));
      return text.substring(start - 1, start - 1 + n);
    }
    case "LEN": {
      const text = stripQuotes(evaluateFormulaInternal(args[0] ?? '""', getCell));
      return formatNumber(text.length);
    }
    case "TRIM": {
      const text = stripQuotes(evaluateFormulaInternal(args[0] ?? '""', getCell));
      return text.trim().replace(/\s+/g, " ");
    }
    case "LOWER": return stripQuotes(evaluateFormulaInternal(args[0] ?? '""', getCell)).toLowerCase();
    case "UPPER": return stripQuotes(evaluateFormulaInternal(args[0] ?? '""', getCell)).toUpperCase();
    case "PROPER": {
      const text = stripQuotes(evaluateFormulaInternal(args[0] ?? '""', getCell));
      return text.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    }
    case "FIND": {
      const findText = stripQuotes(evaluateFormulaInternal(args[0], getCell));
      const within = stripQuotes(evaluateFormulaInternal(args[1], getCell));
      const start = Math.max(1, Math.floor(toNumber(evaluateFormulaInternal(args[2] ?? "1", getCell))));
      const idx = within.indexOf(findText, start - 1);
      return idx >= 0 ? formatNumber(idx + 1) : "#VALUE!";
    }
    case "SEARCH": {
      const findText = stripQuotes(evaluateFormulaInternal(args[0], getCell)).toLowerCase();
      const within = stripQuotes(evaluateFormulaInternal(args[1], getCell)).toLowerCase();
      const start = Math.max(1, Math.floor(toNumber(evaluateFormulaInternal(args[2] ?? "1", getCell))));
      const idx = within.indexOf(findText, start - 1);
      return idx >= 0 ? formatNumber(idx + 1) : "#VALUE!";
    }
    case "REPLACE": {
      const oldText = stripQuotes(evaluateFormulaInternal(args[0], getCell));
      const start = Math.max(1, Math.floor(toNumber(evaluateFormulaInternal(args[1], getCell))));
      const numChars = Math.max(0, Math.floor(toNumber(evaluateFormulaInternal(args[2], getCell))));
      const newText = stripQuotes(evaluateFormulaInternal(args[3] ?? '""', getCell));
      return oldText.substring(0, start - 1) + newText + oldText.substring(start - 1 + numChars);
    }
    case "SUBSTITUTE": {
      const text = stripQuotes(evaluateFormulaInternal(args[0], getCell));
      const oldText = stripQuotes(evaluateFormulaInternal(args[1], getCell));
      const newText = stripQuotes(evaluateFormulaInternal(args[2], getCell));
      const instance = args[3] ? Math.floor(toNumber(evaluateFormulaInternal(args[3], getCell))) : 0;
      if (instance <= 0) return text.split(oldText).join(newText);
      let count = 0;
      return text.replace(new RegExp(oldText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), (m) => {
        count++;
        return count === instance ? newText : m;
      });
    }
    case "TEXT": {
      // Basic number formatting: TEXT(value, "0.00")
      const val = toNumber(evaluateFormulaInternal(args[0] ?? "0", getCell));
      const fmt = stripQuotes(evaluateFormulaInternal(args[1] ?? '"0"', getCell));
      const decimalsMatch = fmt.match(/0+(\.0+)?/);
      const decimals = (decimalsMatch && decimalsMatch[1]) ? decimalsMatch[1].length - 1 : 0;
      return val.toFixed(Math.max(0, decimals));
    }

    // Lookup & Reference
    case "INDEX": {
      // INDEX(range, row, [column])
      if (args.length < 2) return "#ERROR";
      const ids = expandRangeCells(args[0]);
      if (ids.length === 0) return "#REF!";
      const { row: r1, col: c1 } = parseCell(args[0].split(":")[0]);
      const { row: r2, col: c2 } = parseCell(args[0].split(":")[1] || args[0].split(":")[0]);
      const nRows = Math.abs(r2 - r1) + 1;
      const nCols = Math.abs(c2 - c1) + 1;
      const rowIdx = Math.floor(toNumber(evaluateFormulaInternal(args[1], getCell))) - 1;
      const colIdx = args[2] ? Math.floor(toNumber(evaluateFormulaInternal(args[2], getCell))) - 1 : 0;
      if (rowIdx < 0 || colIdx < 0 || rowIdx >= nRows || colIdx >= nCols) return "#REF!";
      const flatIdx = rowIdx * nCols + colIdx;
      return getCell(ids[flatIdx]);
    }
    case "MATCH": {
      // MATCH(lookup_value, lookup_array, [match_type])
      if (args.length < 2) return "#ERROR";
      const lookup = stripQuotes(evaluateFormulaInternal(args[0], getCell));
      const arr = flattenToStrings([args[1]], getCell);
      const type = args[2] ? Math.floor(toNumber(evaluateFormulaInternal(args[2], getCell))) : 0;
      if (type === 0) {
        const idx = arr.findIndex((v) => v === lookup);
        return idx >= 0 ? formatNumber(idx + 1) : "#N/A";
      } else {
        // Simplified approximate: closest less-than for 1, greater-than for -1
        const nums = arr.map(toNumber);
        if (type === 1) {
          let best = -1;
          const target = toNumber(lookup);
          for (let i = 0; i < nums.length; i++) if (nums[i] <= target) best = i;
          return best >= 0 ? formatNumber(best + 1) : "#N/A";
        } else {
          let best = -1;
          const target = toNumber(lookup);
          for (let i = 0; i < nums.length; i++) if (nums[i] >= target) { best = i; break; }
          return best >= 0 ? formatNumber(best + 1) : "#N/A";
        }
      }
    }
    case "VLOOKUP": {
      // VLOOKUP(lookup, table_range, col_index, [range_lookup])
      if (args.length < 3) return "#ERROR";
      const lookup = stripQuotes(evaluateFormulaInternal(args[0], getCell));
      const ids = expandRangeCells(args[1]);
      if (ids.length === 0) return "#REF!";
      const { row: r1, col: c1 } = parseCell(args[1].split(":")[0]);
      const { row: r2, col: c2 } = parseCell(args[1].split(":")[1]);
      const nRows = Math.abs(r2 - r1) + 1;
      const nCols = Math.abs(c2 - c1) + 1;
      const colIndex = Math.floor(toNumber(evaluateFormulaInternal(args[2], getCell)));
      const rangeLookup = args[3] ? coerceBoolean(evaluateFormulaInternal(args[3], getCell)) : false;
      for (let r = 0; r < nRows; r++) {
        const firstCellId = ids[r * nCols + 0];
        const key = getCell(firstCellId);
        if (key === lookup || (rangeLookup && toNumber(key) <= toNumber(lookup))) {
          const idx = r * nCols + (colIndex - 1);
          if (idx < 0 || idx >= ids.length) return "#REF!";
          return getCell(ids[idx]);
        }
      }
      return "#N/A";
    }
    case "HLOOKUP": {
      // HLOOKUP(lookup, table_range, row_index, [range_lookup])
      if (args.length < 3) return "#ERROR";
      const lookup = stripQuotes(evaluateFormulaInternal(args[0], getCell));
      const ids = expandRangeCells(args[1]);
      if (ids.length === 0) return "#REF!";
      const { row: r1, col: c1 } = parseCell(args[1].split(":")[0]);
      const { row: r2, col: c2 } = parseCell(args[1].split(":")[1]);
      const nRows = Math.abs(r2 - r1) + 1;
      const nCols = Math.abs(c2 - c1) + 1;
      const rowIndex = Math.floor(toNumber(evaluateFormulaInternal(args[2], getCell)));
      const rangeLookup = args[3] ? coerceBoolean(evaluateFormulaInternal(args[3], getCell)) : false;
      for (let c = 0; c < nCols; c++) {
        const key = getCell(ids[0 * nCols + c]);
        if (key === lookup || (rangeLookup && toNumber(key) <= toNumber(lookup))) {
          const idx = (rowIndex - 1) * nCols + c;
          if (idx < 0 || idx >= ids.length) return "#REF!";
          return getCell(ids[idx]);
        }
      }
      return "#N/A";
    }
    case "LOOKUP": {
      // Simplified LOOKUP: exact match in vector, return last matching or #N/A
      if (args.length < 2) return "#ERROR";
      const lookup = stripQuotes(evaluateFormulaInternal(args[0], getCell));
      const vec = flattenToStrings([args[1]], getCell);
      let found = "#N/A";
      for (const v of vec) if (v === lookup) found = v;
      return found;
    }
    case "OFFSET": {
      // OFFSET(reference, rows, cols)
      if (args.length < 3) return "#ERROR";
      const base = args[0];
      const rows = Math.floor(toNumber(evaluateFormulaInternal(args[1], getCell)));
      const cols = Math.floor(toNumber(evaluateFormulaInternal(args[2], getCell)));
      const cell = /^[A-Za-z]+\d+/.exec(base)?.[0] ?? "";
      if (!cell) return "#REF!";
      const { row, col } = parseCell(cell);
      const r = row + rows;
      const c = col + cols;
      if (!inBounds(r, c)) return "#REF!";
      return getCell(cellIdFrom(r, c));
    }
    case "INDIRECT": {
      const refText = stripQuotes(evaluateFormulaInternal(args[0], getCell));
      if (/^[A-Za-z]+\d+$/.test(refText)) return getCell(refText);
      const rangeIds = expandRangeCells(refText);
      return rangeIds.length ? getCell(rangeIds[0]) : "#REF!";
    }
    case "CHOOSE": {
      const index = Math.floor(toNumber(evaluateFormulaInternal(args[0], getCell)));
      const choice = args[index] ?? '""';
      return evaluateFormulaInternal(choice, getCell);
    }

    // Logical
    case "IF": {
      if (args.length < 2) return "#ERROR";
      const cond = evaluateCondition(args[0], getCell);
      return cond ? evaluateFormulaInternal(args[1], getCell) : evaluateFormulaInternal(args[2] ?? '""', getCell);
    }
    case "IFS": {
      for (let i = 0; i < args.length; i += 2) {
        const condExpr = args[i];
        const resExpr = args[i + 1];
        if (resExpr === undefined) break;
        if (evaluateCondition(condExpr, getCell)) return evaluateFormulaInternal(resExpr, getCell);
      }
      return "#N/A";
    }
    case "AND": {
      for (const a of args) if (!evaluateCondition(a, getCell)) return "FALSE";
      return "TRUE";
    }
    case "OR": {
      for (const a of args) if (evaluateCondition(a, getCell)) return "TRUE";
      return "FALSE";
    }
    case "NOT": {
      const v = evaluateCondition(args[0] ?? "FALSE", getCell);
      return v ? "FALSE" : "TRUE";
    }
    case "XOR": {
      const vals = args.map((a) => evaluateCondition(a, getCell));
      const trueCount = vals.filter(Boolean).length;
      return trueCount % 2 === 1 ? "TRUE" : "FALSE";
    }
    case "IFERROR": {
      if (args.length < 2) return "#ERROR";
      try {
        const v = evaluateFormulaInternal(args[0], getCell);
        if (String(v).startsWith("#")) return evaluateFormulaInternal(args[1], getCell);
        return v;
      } catch {
        return evaluateFormulaInternal(args[1], getCell);
      }
    }
    case "IFNA": {
      if (args.length < 2) return "#ERROR";
      const v = evaluateFormulaInternal(args[0], getCell);
      return v === "#N/A" ? evaluateFormulaInternal(args[1], getCell) : v;
    }

    // Date & Time
    case "TODAY": return formatDate(new Date());
    case "NOW": {
      const d = new Date();
      return `${formatDate(d)} ${formatTime(d)}`;
    }
    case "DATE": {
      const y = Math.floor(toNumber(evaluateFormulaInternal(args[0], getCell)));
      const m = Math.floor(toNumber(evaluateFormulaInternal(args[1], getCell))) - 1;
      const d = Math.floor(toNumber(evaluateFormulaInternal(args[2], getCell)));
      return formatDate(new Date(y, m, d));
    }
    case "TIME": {
      const h = Math.floor(toNumber(evaluateFormulaInternal(args[0], getCell)));
      const m = Math.floor(toNumber(evaluateFormulaInternal(args[1], getCell)));
      const s = Math.floor(toNumber(evaluateFormulaInternal(args[2], getCell)));
      const dt = new Date();
      dt.setHours(h, m, s, 0);
      return formatTime(dt);
    }
    case "YEAR": {
      const d = parseDate(evaluateFormulaInternal(args[0], getCell));
      return d ? formatNumber(d.getFullYear()) : "#VALUE!";
    }
    case "MONTH": {
      const d = parseDate(evaluateFormulaInternal(args[0], getCell));
      return d ? formatNumber(d.getMonth() + 1) : "#VALUE!";
    }
    case "DAY": {
      const d = parseDate(evaluateFormulaInternal(args[0], getCell));
      return d ? formatNumber(d.getDate()) : "#VALUE!";
    }
    case "HOUR": {
      const d = parseDate(evaluateFormulaInternal(args[0], getCell));
      return d ? formatNumber(d.getHours()) : "#VALUE!";
    }
    case "MINUTE": {
      const d = parseDate(evaluateFormulaInternal(args[0], getCell));
      return d ? formatNumber(d.getMinutes()) : "#VALUE!";
    }
    case "SECOND": {
      const d = parseDate(evaluateFormulaInternal(args[0], getCell));
      return d ? formatNumber(d.getSeconds()) : "#VALUE!";
    }
    case "EOMONTH": {
      const start = parseDate(evaluateFormulaInternal(args[0], getCell));
      const months = Math.floor(toNumber(evaluateFormulaInternal(args[1], getCell)));
      if (!start) return "#VALUE!";
      const d = new Date(start.getFullYear(), start.getMonth() + months + 1, 0);
      return formatDate(d);
    }
    case "WEEKDAY": {
      const d = parseDate(evaluateFormulaInternal(args[0], getCell));
      if (!d) return "#VALUE!";
      // 1=Sunday..7=Saturday
      return formatNumber(d.getDay() + 1);
    }
    case "WORKDAY": {
      const start = parseDate(evaluateFormulaInternal(args[0], getCell));
      const days = Math.floor(toNumber(evaluateFormulaInternal(args[1], getCell)));
      if (!start) return "#VALUE!";
      const d = new Date(start);
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day !== 0 && day !== 6) added++;
      }
      return formatDate(d);
    }
    case "NETWORKDAYS": {
      const start = parseDate(evaluateFormulaInternal(args[0], getCell));
      const end = parseDate(evaluateFormulaInternal(args[1], getCell));
      if (!start || !end) return "#VALUE!";
      let count = 0;
      const d = new Date(start);
      while (d <= end) {
        const day = d.getDay();
        if (day !== 0 && day !== 6) count++;
        d.setDate(d.getDate() + 1);
      }
      return formatNumber(count);
    }
    case "DATEDIF": {
      const start = parseDate(evaluateFormulaInternal(args[0], getCell));
      const end = parseDate(evaluateFormulaInternal(args[1], getCell));
      const unit = stripQuotes(evaluateFormulaInternal(args[2], getCell)).toUpperCase();
      if (!start || !end) return "#VALUE!";
      const ms = Math.max(0, end.getTime() - start.getTime());
      const days = Math.floor(ms / (1000 * 60 * 60 * 24));
      if (unit === "D") return formatNumber(days);
      if (unit === "M") return formatNumber((end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()));
      if (unit === "Y") return formatNumber(end.getFullYear() - start.getFullYear());
      return "#VALUE!";
    }

    // Financial (simplified but correct standard formulas)
    case "PMT": {
      // PMT(rate, nper, pv, [fv=0], [type=0])
      const rate = toNumber(evaluateFormulaInternal(args[0], getCell));
      const nper = toNumber(evaluateFormulaInternal(args[1], getCell));
      const pv = toNumber(evaluateFormulaInternal(args[2], getCell));
      const fv = args[3] ? toNumber(evaluateFormulaInternal(args[3], getCell)) : 0;
      const type = args[4] ? toNumber(evaluateFormulaInternal(args[4], getCell)) : 0;
      let pmt: number;
      if (rate === 0) {
        pmt = -(pv + fv) / nper;
      } else {
        const r1 = Math.pow(1 + rate, nper);
        pmt = -(rate * (pv * r1 + fv)) / ((1 + rate * type) * (r1 - 1));
      }
      return formatNumber(pmt);
    }
    case "PV": {
      const rate = toNumber(evaluateFormulaInternal(args[0], getCell));
      const nper = toNumber(evaluateFormulaInternal(args[1], getCell));
      const pmt = toNumber(evaluateFormulaInternal(args[2], getCell));
      const fv = args[3] ? toNumber(evaluateFormulaInternal(args[3], getCell)) : 0;
      const type = args[4] ? toNumber(evaluateFormulaInternal(args[4], getCell)) : 0;
      let pv: number;
      if (rate === 0) {
        pv = -(pmt * nper + fv);
      } else {
        const r1 = Math.pow(1 + rate, nper);
        pv = -((pmt * (1 + rate * type) * (r1 - 1)) / rate + fv) / r1;
      }
      return formatNumber(pv);
    }
    case "FV": {
      const rate = toNumber(evaluateFormulaInternal(args[0], getCell));
      const nper = toNumber(evaluateFormulaInternal(args[1], getCell));
      const pmt = toNumber(evaluateFormulaInternal(args[2], getCell));
      const pv = args[3] ? toNumber(evaluateFormulaInternal(args[3], getCell)) : 0;
      const type = args[4] ? toNumber(evaluateFormulaInternal(args[4], getCell)) : 0;
      let fv: number;
      if (rate === 0) {
        fv = -(pv + pmt * nper);
      } else {
        const r1 = Math.pow(1 + rate, nper);
        fv = -(pv * r1 + (pmt * (1 + rate * type) * (r1 - 1)) / rate);
      }
      return formatNumber(fv);
    }
    case "NPER": {
      const rate = toNumber(evaluateFormulaInternal(args[0], getCell));
      const pmt = toNumber(evaluateFormulaInternal(args[1], getCell));
      const pv = toNumber(evaluateFormulaInternal(args[2], getCell));
      const fv = args[3] ? toNumber(evaluateFormulaInternal(args[3], getCell)) : 0;
      const type = args[4] ? toNumber(evaluateFormulaInternal(args[4], getCell)) : 0;
      if (rate === 0) return formatNumber(-(pv + fv) / pmt);
      const a = pmt * (1 + rate * type) / rate;
      const b = -(pv + a);
      const n = Math.log((a - fv) / b) / Math.log(1 + rate);
      return formatNumber(n);
    }
    case "NPV": {
      const rate = toNumber(evaluateFormulaInternal(args[0], getCell));
      const cashflows = flattenToNumbers(args.slice(1), getCell);
      let sum = 0;
      for (let i = 0; i < cashflows.length; i++) {
        sum += cashflows[i] / Math.pow(1 + rate, i + 1);
      }
      return formatNumber(sum);
    }

    // Statistical
    case "MEDIAN": {
      const nums = flattenToNumbers(args, getCell);
      if (nums.length === 0) return "#DIV/0!";
      const mid = percentile(nums, 0.5);
      return formatNumber(mid);
    }
    case "MODE": {
      const nums = flattenToNumbers(args, getCell);
      const freq: Record<string, number> = {};
      nums.forEach((n) => { const k = String(n); freq[k] = (freq[k] || 0) + 1; });
      let bestKey = null, bestCount = 0;
      Object.entries(freq).forEach(([k, c]) => { if (c > bestCount) { bestCount = c; bestKey = k; } });
      return bestKey !== null ? bestKey : "#N/A";
    }
    case "STDEV": {
      const x = flattenToNumbers(args, getCell);
      if (x.length < 2) return "#DIV/0!";
      const mean = x.reduce((a, b) => a + b, 0) / x.length;
      const variance = x.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / (x.length - 1);
      return formatNumber(Math.sqrt(variance));
    }
    case "STDEVP": {
      const x = flattenToNumbers(args, getCell);
      if (x.length === 0) return "#DIV/0!";
      const mean = x.reduce((a, b) => a + b, 0) / x.length;
      const variance = x.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / x.length;
      return formatNumber(Math.sqrt(variance));
    }
    case "VAR": {
      const x = flattenToNumbers(args, getCell);
      if (x.length < 2) return "#DIV/0!";
      const mean = x.reduce((a, b) => a + b, 0) / x.length;
      const variance = x.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / (x.length - 1);
      return formatNumber(variance);
    }
    case "VARP": {
      const x = flattenToNumbers(args, getCell);
      if (x.length === 0) return "#DIV/0!";
      const mean = x.reduce((a, b) => a + b, 0) / x.length;
      const variance = x.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / x.length;
      return formatNumber(variance);
    }
    case "PERCENTILE": {
      const arr = flattenToNumbers([args[0]], getCell);
      const k = toNumber(evaluateFormulaInternal(args[1], getCell));
      return formatNumber(percentile(arr, k));
    }
    case "QUARTILE": {
      const arr = flattenToNumbers([args[0]], getCell);
      const q = Math.floor(toNumber(evaluateFormulaInternal(args[1], getCell)));
      const k = q === 0 ? 0 : q === 1 ? 0.25 : q === 2 ? 0.5 : q === 3 ? 0.75 : 1.0;
      return formatNumber(percentile(arr, k));
    }

    // Array / Dynamic (returned as single-cell strings in MapleSheet)
    case "UNIQUE": {
      const vals = flattenToStrings(args, getCell);
      const seen = new Set<string>();
      const out: string[] = [];
      vals.forEach((v) => { if (!seen.has(v)) { seen.add(v); out.push(v); } });
      return out.join(", ");
    }
    case "SEQUENCE": {
      const rows = Math.floor(toNumber(evaluateFormulaInternal(args[0] ?? "1", getCell)));
      const cols = Math.floor(toNumber(evaluateFormulaInternal(args[1] ?? "1", getCell)));
      const start = toNumber(evaluateFormulaInternal(args[2] ?? "1", getCell));
      const step = toNumber(evaluateFormulaInternal(args[3] ?? "1", getCell));
      let cur = start;
      const parts: string[] = [];
      for (let r = 0; r < rows; r++) {
        const rowVals: string[] = [];
        for (let c = 0; c < cols; c++) {
          rowVals.push(formatNumber(cur));
          cur += step;
        }
        parts.push(rowVals.join(", "));
      }
      return parts.join(" ; ");
    }
    case "FILTER": {
      // FILTER(range, include_range_as_criteria)
      if (args.length < 2) return "#ERROR";
      const vals = flattenToStrings([args[0]], getCell);
      const includeRaw = flattenToStrings([args[1]], getCell);
      const out: string[] = [];
      for (let i = 0; i < vals.length; i++) {
        if (coerceBoolean(includeRaw[i] ?? "")) out.push(vals[i]);
      }
      return out.join(", ");
    }
    case "SORT": {
      // SORT(range, [sort_index=1], [order=1])
      const ids = expandRangeCells(args[0]);
      if (ids.length === 0) return "";
      const { row: r1, col: c1 } = parseCell(args[0].split(":")[0]);
      const { row: r2, col: c2 } = parseCell(args[0].split(":")[1] || args[0].split(":")[0]);
      const nRows = Math.abs(r2 - r1) + 1;
      const nCols = Math.abs(c2 - c1) + 1;
      const sortIndex = args[1] ? Math.floor(toNumber(evaluateFormulaInternal(args[1], getCell))) - 1 : 0;
      const order = args[2] ? Math.floor(toNumber(evaluateFormulaInternal(args[2], getCell))) : 1;
      const rows: string[][] = [];
      for (let r = 0; r < nRows; r++) {
        const rowVals: string[] = [];
        for (let c = 0; c < nCols; c++) rowVals.push(getCell(ids[r * nCols + c]));
        rows.push(rowVals);
      }
      rows.sort((a, b) => {
        const av = a[sortIndex] ?? "";
        const bv = b[sortIndex] ?? "";
        const cmp = toNumber(av) - toNumber(bv);
        return order === -1 ? -cmp : cmp;
      });
      return rows.map((row) => row.join(", ")).join(" ; ");
    }
    case "SORTBY": {
      // SORTBY(range, by_array, [order=1])
      const base = flattenToStrings([args[0]], getCell);
      const by = flattenToStrings([args[1]], getCell);
      const order = args[2] ? Math.floor(toNumber(evaluateFormulaInternal(args[2], getCell))) : 1;
      const idxs = base.map((_, i) => i);
      idxs.sort((i, j) => {
        const cmp = toNumber(by[i]) - toNumber(by[j]);
        return order === -1 ? -cmp : cmp;
      });
      return idxs.map((i) => base[i]).join(", ");
    }
    case "LAMBDA": {
      // Not supported in this simplified engine
      return "#N/A";
    }
    default:
      return "#NAME?";
  }
}

const evaluateCondition = (expr: string, getCell: GetCellValue, scopeVars?: Record<string, string>): boolean => {
  const s = expr.trim();
  // Support basic comparisons: A1>5, 3<=B2, "x"="y"
  const m = s.match(/^(.*?)(>=|<=|<>|=|>|<)(.*)$/);
  if (m) {
    const left = evaluateFormulaInternal(m[1].trim(), getCell, scopeVars);
    const op = m[2];
    const right = evaluateFormulaInternal(m[3].trim(), getCell, scopeVars);
    const lNum = toNumber(left);
    const rNum = toNumber(right);
    const bothNumeric = !(isNaN(lNum) || isNaN(rNum));
    switch (op) {
      case ">": return bothNumeric ? lNum > rNum : left > right;
      case "<": return bothNumeric ? lNum < rNum : left < right;
      case ">=": return bothNumeric ? lNum >= rNum : left >= right;
      case "<=": return bothNumeric ? lNum <= rNum : left <= right;
      case "=": return bothNumeric ? lNum === rNum : left === right;
      case "<>": return bothNumeric ? lNum !== rNum : left !== right;
      default: return false;
    }
  }
  // Single token -> truthy check
  const v = evaluateFormulaInternal(s, getCell, scopeVars);
  return coerceBoolean(v);
};

function evaluateFormulaInternal(formula: string, getCell: GetCellValue, scopeVars?: Record<string, string>): string {
  const f = String(formula ?? "").trim();
  if (f.length === 0) return "";
  // If wrapped in quotes -> string literal
  if (/^".*"$/.test(f)) return stripQuotes(f);

  // Function call?
  const funcMatch = f.match(/^([A-Za-z]+)\((.*)\)$/s);
  if (funcMatch) {
    const name = funcMatch[1];
    const argsStr = funcMatch[2];
    return callFunction(name, argsStr, getCell, scopeVars);
  }

  // Variables (LET)
  if (scopeVars && f in scopeVars) {
    return scopeVars[f];
  }

  // Cell or range?
  if (/^[A-Za-z]+\d+:[A-Za-z]+\d+$/.test(f)) {
    const ids = expandRangeCells(f);
    return ids.length ? getCell(ids[0]) : "#REF!";
  }
  if (/^[A-Za-z]+\d+$/.test(f)) {
    return getCell(f);
  }

  // Arithmetic expression: replace cell refs with numeric values
  const replaced = f.replace(/[A-Za-z]+\d+:[A-Za-z]+\d+|[A-Za-z]+\d+/g, (ref) => {
    if (ref.includes(":")) {
      const nums = expandRangeCells(ref).map((id) => toNumber(getCell(id)));
      return nums.join("+"); // sum-like behavior if used directly (e.g., A1:A3 + 5)
    } else {
      return String(toNumber(getCell(ref)));
    }
  });

  try {
    // Evaluate basic arithmetic safely
    // eslint-disable-next-line no-new-func
    const result = Function(`"use strict"; return (${replaced})`)();
    if (result === undefined || result === null) return "";
    if (typeof result === "number") return formatNumber(result);
    return String(result);
  } catch {
    return "#ERROR";
  }
}

export const evaluateFormula = (formula: string, getCell: GetCellValue): string => {
  const f = String(formula ?? "").trim();
  if (!f.startsWith("=")) {
    return f;
  }
  const inner = f.slice(1);
  return evaluateFormulaInternal(inner, getCell);
};